import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Target,
  CheckCircle2,
  Circle,
  Flame,
  Calendar,
  Moon,
  Users,
  Trophy,
  Star,
  Loader2
} from 'lucide-react';

const API = '/api';

const MISSION_ICONS = {
  daily_checkin: Calendar,
  daily_activity: Flame,
  daily_sleep: Moon,
  community_post: Users,
  streak_3: Flame,
  activity_5: Target,
  community_engage: Users,
};

const MissionsPage = () => {
  const [missions, setMissions] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const [missionsRes, rewardsRes] = await Promise.all([
        axios.get(`${API}/missions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rewards/level`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMissions(missionsRes.data);
      setRewards(rewardsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLevelIcon = (level) => {
    const icons = {
      bronze: '🥉',
      silver: '🥈',
      gold: '🥇'
    };
    return icons[level] || '⭐';
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-green-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
          <Target className="h-8 w-8 text-green-600" />
          Misyonlar & Ödüller
        </h1>

        {/* Reward Level Card */}
        {rewards && (
          <Card className="mb-6" style={{ borderColor: rewards.current_level?.color }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{getLevelIcon(rewards.current_level?.level)}</span>
                  <div>
                    <h3 className="text-xl font-bold" style={{ color: rewards.current_level?.color }}>
                      {rewards.current_level?.name} Seviye
                    </h3>
                    <p className="text-sm text-gray-500">{rewards.total_points} puan</p>
                  </div>
                </div>
                <Trophy className="h-8 w-8" style={{ color: rewards.current_level?.color }} />
              </div>

              {rewards.next_level && (
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>{rewards.current_level?.name}</span>
                    <span>{rewards.next_level?.name}'e {rewards.points_to_next} puan</span>
                  </div>
                  <Progress value={rewards.progress_percentage} className="h-2" />
                </div>
              )}

              {/* Perks */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-sm font-medium mb-2">Avantajların:</p>
                <div className="flex flex-wrap gap-2">
                  {rewards.current_level?.perks?.map((perk, i) => (
                    <Badge key={i} variant="outline">
                      <Star className="h-3 w-3 mr-1" />
                      {perk}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Today's Points */}
        <Card className="mb-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-green-100">Bugün kazanılan</p>
              <p className="text-3xl font-bold">{missions?.today_points || 0} puan</p>
            </div>
            <div className="text-right">
              <p className="text-green-100">Toplam</p>
              <p className="text-3xl font-bold">{missions?.total_points || 0}</p>
            </div>
          </CardContent>
        </Card>

        {/* Daily Missions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              Günlük Misyonlar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {missions?.daily_missions?.map((mission) => {
              const Icon = MISSION_ICONS[mission.id] || Target;
              return (
                <div
                  key={mission.id}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    mission.completed ? 'bg-green-50' : 'bg-gray-50'
                  }`}
                >
                  {mission.completed ? (
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                  ) : (
                    <Circle className="h-6 w-6 text-gray-300" />
                  )}
                  <Icon className={`h-5 w-5 ${mission.completed ? 'text-green-600' : 'text-gray-400'}`} />
                  <div className="flex-1">
                    <p className={`font-medium ${mission.completed ? 'text-green-700' : ''}`}>
                      {mission.title}
                    </p>
                    <p className="text-sm text-gray-500">{mission.description}</p>
                  </div>
                  <Badge className={mission.completed ? 'bg-green-100 text-green-700' : ''}>
                    +{mission.points} puan
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Weekly Missions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              Haftalık Misyonlar
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {missions?.weekly_missions?.map((mission) => {
              const Icon = MISSION_ICONS[mission.id] || Target;
              return (
                <div
                  key={mission.id}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    mission.completed ? 'bg-orange-50' : 'bg-gray-50'
                  }`}
                >
                  {mission.completed ? (
                    <CheckCircle2 className="h-6 w-6 text-orange-500" />
                  ) : (
                    <Circle className="h-6 w-6 text-gray-300" />
                  )}
                  <Icon className={`h-5 w-5 ${mission.completed ? 'text-orange-600' : 'text-gray-400'}`} />
                  <div className="flex-1">
                    <p className={`font-medium ${mission.completed ? 'text-orange-700' : ''}`}>
                      {mission.title}
                    </p>
                    <p className="text-sm text-gray-500">{mission.description}</p>
                  </div>
                  <Badge className={mission.completed ? 'bg-orange-100 text-orange-700' : ''}>
                    +{mission.points} puan
                  </Badge>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MissionsPage;
